/* eslint-disable ember/no-jquery */
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { hash } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { cancel, later, run, schedule } from "@ember/runloop";
import { service } from "@ember/service";
import {
  computePosition,
  flip,
  limitShift,
  offset,
  shift,
} from "@floating-ui/dom";
import curryComponent from "ember-curry-component";
import $ from "jquery";
import { Promise } from "rsvp";
import lazyHash from "discourse/helpers/lazy-hash";
import { isTesting } from "discourse/lib/environment";
import { emojiUrlFor } from "discourse/lib/text";
import closeOnClickOutside from "discourse/modifiers/close-on-click-outside";
import { and, eq, not } from "discourse/truth-helpers";
import { i18n } from "discourse-i18n";
import CustomReaction from "../models/discourse-reactions-custom-reaction";
import DiscourseReactionsCounter from "./discourse-reactions-counter";
import DiscourseReactionsDoubleButton from "./discourse-reactions-double-button";
import DiscourseReactionsPicker from "./discourse-reactions-picker";
import DiscourseReactionsReactionButton from "./discourse-reactions-reaction-button";

const VIBRATE_DURATION = 5;

let activeReactionsComponent;

export function resetCurrentReaction() {
  activeReactionsComponent = null;
}

function buildFakeReaction(reactionId) {
  const img = document.createElement("img");
  img.src = emojiUrlFor(reactionId);
  img.classList.add(
    "btn-toggle-reaction-emoji",
    "reaction-button",
    "fake-reaction"
  );

  return img;
}

function moveReactionAnimation(
  postContainer,
  reactionId,
  startPosition,
  endPosition,
  complete
) {
  if (isTesting()) {
    return;
  }

  const fakeReaction = buildFakeReaction(reactionId);
  const reactionButton = postContainer.querySelector(".reaction-button");

  reactionButton.appendChild(fakeReaction);

  let done = () => {
    fakeReaction.remove();
    complete();
  };

  fakeReaction.style.top = startPosition;
  fakeReaction.style.opacity = 0;

  $(fakeReaction).animate(
    {
      top: endPosition,
      opacity: 1,
    },
    {
      duration: 350,
      complete: done,
    },
    "swing"
  );
}

function addReaction(list, reactionId, complete) {
  moveReactionAnimation(list, reactionId, "-50px", "8px", complete);
}

function dropReaction(list, reactionId, complete) {
  moveReactionAnimation(list, reactionId, "8px", "42px", complete);
}

function scaleReactionAnimation(mainReaction, start, end, complete) {
  if (isTesting()) {
    return run(this, complete);
  }

  return $(mainReaction)
    .stop()
    .css("textIndent", start)
    .animate(
      { textIndent: end },
      {
        complete,
        step(now) {
          $(this)
            .css("transform", `scale(${now})`)
            .addClass("d-icon-d-unliked")
            .removeClass("d-icon-d-liked");
        },
        duration: 150,
      },
      "linear"
    );
}

export default class DiscourseReactionsActions extends Component {
  @service dialog;
  @service capabilities;
  @service siteSettings;
  @service site;
  @service currentUser;
  @service appEvents;

  @tracked reactionsPickerExpanded = false;
  @tracked statePanelExpanded = false;
  @tracked clickOutsideDisabled = false;

  containerElement = null;

  get data() {
    return this.args.post;
  }

  get classes() {
    if (!this.data?.reactions) {
      return;
    }

    const hasReactions = this.data.reactions.length;
    const hasReacted = this.data.current_user_reaction;
    const customReactionUsed =
      this.data.reactions.length &&
      this.data.reactions.filter(
        (reaction) =>
          reaction.id !==
          this.siteSettings.discourse_reactions_reaction_for_like
      ).length;
    const classes = [];

    if (customReactionUsed) {
      classes.push("custom-reaction-used");
    }

    if (this.data.yours) {
      classes.push("my-post");
    }

    if (hasReactions) {
      classes.push("has-reactions");
    }

    if (hasReacted) {
      classes.push("has-reacted");
    }

    if (this.data.current_user_used_main_reaction) {
      classes.push("has-used-main-reaction");
    }

    if (
      (!this.data.current_user_reaction ||
        this.data.current_user_reaction.can_undo) &&
      this.data.likeAction?.canToggle
    ) {
      classes.push("can-toggle-reaction");
    }

    return classes.join(" ");
  }

  @action
  toggleReactions(event) {
    if (!this.reactionsPickerExpanded) {
      if (this.statePanelExpanded) {
        this.scheduleExpand("expandReactionsPicker");
      } else {
        this.expandReactionsPicker(event);
      }
    }
  }

  @action
  touchStart() {
    this._validTouch = true;

    cancel(this._touchTimeout);

    if (this.capabilities.touch) {
      document.documentElement?.classList?.toggle(
        "discourse-reactions-no-select",
        true
      );

      this._touchStartAt = Date.now();
      this._touchTimeout = later(() => {
        this._touchStartAt = null;
        this.toggleReactions();
      }, 400);
      return false;
    }
  }

  @action
  touchMove() {
    // if users move while touching we consider it as a scroll and don't want to
    // trigger the reaction or the picker
    this._validTouch = false;
    cancel(this._touchTimeout);
  }

  @action
  touchEnd(event) {
    cancel(this._touchTimeout);

    if (!this._validTouch) {
      return;
    }

    if (this.capabilities.touch) {
      if (event.changedTouches.length) {
        const endTarget = document.elementFromPoint(
          event.changedTouches[0].clientX,
          event.changedTouches[0].clientY
        );

        if (endTarget) {
          const parentNode = endTarget.parentNode;

          if (endTarget.classList.contains("pickable-reaction")) {
            endTarget.click();
            return;
          } else if (
            parentNode &&
            parentNode.classList.contains("pickable-reaction")
          ) {
            parentNode.click();
            return;
          }
        }
      }

      const duration = Date.now() - (this._touchStartAt || 0);
      this._touchStartAt = null;
      if (duration > 400) {
        if (
          event &&
          event.target &&
          event.target.classList.contains("discourse-reactions-reaction-button")
        ) {
          this.toggleReactions(event);
        }
      } else {
        if (
          event.target &&
          (event.target.classList.contains(
            "discourse-reactions-reaction-button"
          ) ||
            event.target.classList.contains("reaction-button"))
        ) {
          const data = this.data;
          this.toggleFromButton({
            reaction: data.current_user_reaction
              ? data.current_user_reaction.id
              : this.siteSettings.discourse_reactions_reaction_for_like,
          });
        }
      }
    }
  }

  @action
  toggle(params) {
    if (!this.currentUser) {
      if (this.args.showLogin) {
        return this.args.showLogin();
      }
    }

    if (
      !this.data.current_user_reaction ||
      (this.data.current_user_reaction.can_undo &&
        this.data.likeAction?.canToggle)
    ) {
      if (this.capabilities.userHasBeenActive && this.capabilities.canVibrate) {
        navigator.vibrate(VIBRATE_DURATION);
      }

      const pickedReaction = this.containerElement?.querySelector(
        `.discourse-reactions-picker .pickable-reaction.${CSS.escape(
          params.reaction
        )} .emoji`
      );

      const scales = [1.0, 1.75];
      return new Promise((resolve) => {
        if (!pickedReaction) {
          const savedState = this._captureState(this.data);
          this.toggleReaction(params);

          CustomReaction.toggle(this.data, params.reaction, this.appEvents)
            .then(resolve)
            .catch((err) => {
              this.dialog.alert(this._extractErrors(err));
              this._rollbackState(this.data, savedState);
            });
        } else {
          scaleReactionAnimation(pickedReaction, scales[0], scales[1], () => {
            scaleReactionAnimation(pickedReaction, scales[1], scales[0], () => {
              const postContainer = this.containerElement;

              if (
                this.data.current_user_reaction &&
                this.data.current_user_reaction.id === params.reaction
              ) {
                const savedState = this._captureState(this.data);
                this.toggleReaction(params);

                later(() => {
                  dropReaction(postContainer, params.reaction, () => {
                    return CustomReaction.toggle(
                      this.data,
                      params.reaction,
                      this.appEvents
                    )
                      .then(resolve)
                      .catch((err) => {
                        this.dialog.alert(this._extractErrors(err));
                        this._rollbackState(this.data, savedState);
                      });
                  });
                }, 100);
              } else {
                const savedState = this._captureState(this.data);
                addReaction(postContainer, params.reaction, () => {
                  this.toggleReaction(params);

                  CustomReaction.toggle(
                    this.data,
                    params.reaction,
                    this.appEvents
                  )
                    .then(resolve)
                    .catch((err) => {
                      this.dialog.alert(this._extractErrors(err));
                      this._rollbackState(this.data, savedState);
                    });
                });
              }
            });
          });
        }
      }).finally(() => {
        this.collapseAllPanels();
      });
    }
  }

  toggleReaction(attrs) {
    this.collapseAllPanels();

    if (
      this.data.current_user_reaction &&
      !this.data.current_user_reaction.can_undo &&
      !this.data.likeAction?.canToggle
    ) {
      return;
    }

    if (this.data.current_user_reaction) {
      this.data.reactions.every((reaction, index) => {
        if (
          reaction.count <= 1 &&
          reaction.id === this.data.current_user_reaction.id
        ) {
          this.data.reactions.splice(index, 1);
          return false;
        } else if (reaction.id === this.data.current_user_reaction.id) {
          this.data.reactions[index].count -= 1;

          return false;
        }

        return true;
      });
    }

    if (
      attrs.reaction &&
      (!this.data.current_user_reaction ||
        attrs.reaction !== this.data.current_user_reaction.id)
    ) {
      let isAvailable = false;

      this.data.reactions.every((reaction, index) => {
        if (reaction.id === attrs.reaction) {
          this.data.reactions[index].count += 1;
          isAvailable = true;
          return false;
        }
        return true;
      });

      if (!isAvailable) {
        const newReaction = {
          id: attrs.reaction,
          type: "emoji",
          count: 1,
        };

        const tempReactions = Object.assign([], this.data.reactions);

        tempReactions.push(newReaction);

        //sorts reactions and get index of new reaction
        const newReactionIndex = tempReactions
          .sort((reaction1, reaction2) => {
            if (reaction1.count > reaction2.count) {
              return -1;
            }
            if (reaction1.count < reaction2.count) {
              return 1;
            }

            //if count is same, sort it by id
            if (reaction1.id > reaction2.id) {
              return 1;
            }
            if (reaction1.id < reaction2.id) {
              return -1;
            }
          })
          .indexOf(newReaction);

        this.data.reactions.splice(newReactionIndex, 0, newReaction);
      }

      if (!this.data.current_user_reaction) {
        this.data.reaction_users_count += 1;
      }

      this.data.current_user_reaction = {
        id: attrs.reaction,
        type: "emoji",
        can_undo: true,
      };
    } else {
      this.data.reaction_users_count -= 1;
      this.data.current_user_reaction = null;
    }

    if (
      this.data.current_user_reaction &&
      this.data.current_user_reaction.id ===
        this.siteSettings.discourse_reactions_reaction_for_like
    ) {
      this.data.current_user_used_main_reaction = true;
    } else {
      this.data.current_user_used_main_reaction = false;
    }
    // Trigger re-render for anything autotracking reactions.
    // In future, we should make reactions a deeply-trackable structure.
    // eslint-disable-next-line no-self-assign
    this.data.reactions = this.data.reactions;
  }

  @action
  toggleFromButton(attrs) {
    if (!this.currentUser) {
      if (this.args.showLogin) {
        return this.args.showLogin();
      }
    }

    this.collapseAllPanels();

    const mainReactionName =
      this.siteSettings.discourse_reactions_reaction_for_like;
    const current_user_reaction = this.data.current_user_reaction;

    if (
      this.data.likeAction &&
      !(this.data.likeAction.canToggle || this.data.likeAction.can_undo)
    ) {
      return;
    }

    if (
      this.data.current_user_reaction &&
      !this.data.current_user_reaction.can_undo
    ) {
      return;
    }

    if (!this.currentUser || this.data.user_id === this.currentUser.id) {
      return;
    }

    if (this.capabilities.userHasBeenActive && this.capabilities.canVibrate) {
      navigator.vibrate(VIBRATE_DURATION);
    }

    if (current_user_reaction && current_user_reaction.id === attrs.reaction) {
      const savedState = this._captureState(this.data);
      this.toggleReaction(attrs);
      return CustomReaction.toggle(
        this.data,
        attrs.reaction,
        this.appEvents
      ).catch((e) => {
        this.dialog.alert(this._extractErrors(e));
        this._rollbackState(this.data, savedState);
      });
    }

    let selector;
    if (
      this.data.reactions &&
      this.data.reactions.length === 1 &&
      this.data.reactions[0].id === mainReactionName
    ) {
      selector = `.discourse-reactions-double-button .discourse-reactions-reaction-button .d-icon`;
    } else {
      if (!attrs.reaction || attrs.reaction === mainReactionName) {
        selector = `.discourse-reactions-reaction-button .d-icon`;
      } else {
        selector = `.discourse-reactions-reaction-button .reaction-button .btn-toggle-reaction-emoji`;
      }
    }

    const mainReaction = this.containerElement?.querySelector(selector);

    const scales = [1.0, 1.5];
    const savedState = this._captureState(this.data);
    return new Promise((resolve) => {
      scaleReactionAnimation(mainReaction, scales[0], scales[1], () => {
        scaleReactionAnimation(mainReaction, scales[1], scales[0], () => {
          this.toggleReaction(attrs);

          let toggleReaction =
            attrs.reaction && attrs.reaction !== mainReactionName
              ? attrs.reaction
              : this.siteSettings.discourse_reactions_reaction_for_like;

          CustomReaction.toggle(this.data, toggleReaction, this.appEvents)
            .then(resolve)
            .catch((err) => {
              this.dialog.alert(this._extractErrors(err));
              this._rollbackState(this.data, savedState);
            });
        });
      });
    });
  }

  @action
  cancelCollapse() {
    cancel(this._collapseHandler);
  }

  @action
  disableClickOutside() {
    this.clickOutsideDisabled = true;
  }

  @action
  enableClickOutside() {
    this.clickOutsideDisabled = false;
  }

  @action
  cancelExpand() {
    cancel(this._expandHandler);
  }

  scheduleExpand(handler) {
    this.cancelExpand();

    this._expandHandler = later(this, this[handler], 250);
  }

  @action
  scheduleCollapse(handler) {
    this.cancelCollapse();

    this._collapseHandler = later(this, this[handler], 500);
  }

  get elementId() {
    if (!this.data?.id) {
      return null;
    }
    return `discourse-reactions-actions-${this.data.id}-${
      this.args.position || "right"
    }`;
  }

  @action
  clickOutside() {
    if (this.clickOutsideDisabled) {
      return;
    }
    if (this.reactionsPickerExpanded || this.statePanelExpanded) {
      this.collapseAllPanels();
    }
  }

  expandReactionsPicker() {
    cancel(this._collapseHandler);
    activeReactionsComponent?.collapseAllPanels();
    this.statePanelExpanded = false;
    this.reactionsPickerExpanded = true;
    this.updateReactionsPickerPopover();
  }

  @action
  expandStatePanel() {
    cancel(this._collapseHandler);
    activeReactionsComponent?.collapseAllPanels();
    this.statePanelExpanded = true;
    this.reactionsPickerExpanded = false;
    this.updateReactionsStatePanel();
  }

  @action
  collapseStatePanel() {
    cancel(this._collapseHandler);
    this._collapseHandler = null;
    this.statePanelExpanded = false;
  }

  collapseReactionsPicker() {
    cancel(this._collapseHandler);
    this._collapseHandler = null;
    this.reactionsPickerExpanded = false;
  }

  @action
  collapseAllPanels() {
    cancel(this._collapseHandler);
    document.documentElement?.classList?.toggle(
      "discourse-reactions-no-select",
      false
    );
    this._collapseHandler = null;
    this.statePanelExpanded = false;
    this.reactionsPickerExpanded = false;
  }

  @action
  updateReactionsPickerPopover() {
    this.showPopover(
      ".discourse-reactions-reaction-button",
      ".discourse-reactions-picker"
    );
  }

  @action
  updateReactionsStatePanel() {
    this.showPopover(
      ".discourse-reactions-counter",
      ".discourse-reactions-state-panel"
    );
  }

  showPopover(referenceSelector, floatingSelector) {
    schedule("afterRender", () => {
      const referenceElement =
        this.containerElement?.querySelector(referenceSelector);
      const floatingElement =
        this.containerElement?.querySelector(floatingSelector);

      if (!floatingElement) {
        return;
      }

      computePosition(referenceElement, floatingElement, {
        placement: "top",
        middleware: [
          offset(-5),
          shift({ limiter: limitShift() }),
          flip({ padding: 5 }),
        ],
      }).then(({ x, y }) => {
        Object.assign(floatingElement.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });

      activeReactionsComponent = this;
    });
  }

  _captureState(post) {
    return {
      current_user_reaction: post.current_user_reaction
        ? { ...post.current_user_reaction }
        : null,
      current_user_used_main_reaction: post.current_user_used_main_reaction,
      reactions: post.reactions.map((r) => ({ ...r })),
      reaction_users_count: post.reaction_users_count,
    };
  }

  _rollbackState(post, savedState) {
    post.current_user_reaction = savedState.current_user_reaction;
    post.current_user_used_main_reaction =
      savedState.current_user_used_main_reaction;
    post.reactions = savedState.reactions;
    post.reaction_users_count = savedState.reaction_users_count;
  }

  _extractErrors(e) {
    const xhr = e.xhr || e.jqXHR;

    if (!xhr || !xhr.status) {
      return i18n("errors.desc.network");
    }

    if (
      xhr.status === 429 &&
      xhr.responseJSON &&
      xhr.responseJSON.errors &&
      xhr.responseJSON.errors[0]
    ) {
      return xhr.responseJSON.errors[0];
    } else if (xhr.status === 403) {
      return i18n("discourse_reactions.reaction.forbidden");
    } else {
      return i18n("errors.desc.unknown");
    }
  }

  get onlyOneMainReaction() {
    return (
      this.data.reactions?.length === 1 &&
      this.data.reactions[0].id ===
        this.siteSettings.discourse_reactions_reaction_for_like
    );
  }

  get showReactionsPicker() {
    return (
      this.currentUser &&
      this.data.user_id !== this.currentUser.id &&
      this.reactionsPickerExpanded
    );
  }

  @action
  registerContainerElement(element) {
    this.containerElement = element;
  }

  <template>
    <div
      id={{this.elementId}}
      class="discourse-reactions-actions {{this.classes}}"
      {{on "touchstart" this.touchStart}}
      {{on "touchmove" this.touchMove}}
      {{on "touchend" this.touchEnd}}
      {{closeOnClickOutside this.clickOutside}}
      {{didInsert this.registerContainerElement}}
    >
      {{#let
        (hash
          counter=(curryComponent
            DiscourseReactionsCounter
            (lazyHash
              post=this.data
              position=@position
              reactionsPickerExpanded=this.reactionsPickerExpanded
              statePanelExpanded=this.statePanelExpanded
              expandStatePanel=this.expandStatePanel
              collapseStatePanel=this.collapseStatePanel
              cancelCollapse=this.cancelCollapse
              scheduleCollapse=this.scheduleCollapse
              updatePopover=this.updateReactionsStatePanel
              collapseAllPanels=this.collapseAllPanels
            )
          )
          button=(curryComponent
            DiscourseReactionsReactionButton
            (lazyHash
              post=this.data
              position=@position
              cancelCollapse=this.cancelCollapse
              toggleFromButton=this.toggleFromButton
              toggleReactions=this.toggleReactions
              cancelExpand=this.cancelExpand
              scheduleCollapse=this.scheduleCollapse
            )
          )
        )
        as |components|
      }}
        {{#if this.showReactionsPicker}}
          <DiscourseReactionsPicker
            @post={{this.data}}
            @scheduleCollapse={{this.scheduleCollapse}}
            @cancelCollapse={{this.cancelCollapse}}
            @disableClickOutside={{this.disableClickOutside}}
            @enableClickOutside={{this.enableClickOutside}}
            @reactionsPickerExpanded={{this.reactionsPickerExpanded}}
            @toggle={{this.toggle}}
          />
        {{/if}}

        {{#if (eq @position "left")}}
          <components.counter />
        {{else if this.onlyOneMainReaction}}
          <DiscourseReactionsDoubleButton
            @post={{this.data}}
            @counterComponent={{components.counter}}
            @buttonComponent={{components.button}}
          />
        {{else if this.site.mobileView}}
          {{#if (not this.data.yours)}}
            <components.counter />
            <components.button />
          {{else if
            (and this.data.yours this.data.reactions this.data.reactions.length)
          }}
            <components.counter />
          {{/if}}
        {{else if (not this.data.yours)}}
          <components.button />
        {{/if}}
      {{/let}}
    </div>
  </template>
}
